import React, { Component } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import PropTypes from 'prop-types';
import UserSection from './UserSection';
import WorkPreferences from './WorkPreferences';
import AnswersContainer from '../AnswersContainer';
import AnswerSection from '../common/AnswerSection';
import Reviews from './Reviews';
import PastProjects from './PastProjects';
import { getRequest, postRequest, putRequest } from '../../requestUtils';
import './ConsultantProfile.scss';

class ConsultantProfile extends Component {
  constructor(props) {
    super(props);

    const { user: { consultant_profile: { roles_available } } } = this.props;
    this.state = {
      fullView: props.authorized,
      editMode: false,
      confirmationSent: false,
      percentage: props.percentage,
      rolesAvailable: roles_available,
      isPublic: props.public,
      availableNow: props.availableNow,
      hourlyRate: props.hourlyRate,
      suggestedTags: props.suggestedTags,
      showSuggestionModal: '',
    };

    this.toggleFullView = this.toggleFullView.bind(this);
    this.togglePublic = this.togglePublic.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.updateCompletionPercentage = this.updateCompletionPercentage.bind(this);
    this.updateAvailability = this.updateAvailability.bind(this);
    this.updateHourlyRate = this.updateHourlyRate.bind(this);
    this.updateSuggestedTags = this.updateSuggestedTags.bind(this);
  }

  toggleFullView() {
    const { fullView } = this.state;
    this.setState({ fullView: !fullView });
  }

  toggleEditMode() {
    this.setState({ editMode: !this.state.editMode });
  }

  togglePublic() {
    const { isPublic } = this.state;
    const { updatePublicUrl, csrfToken } = this.props;
    putRequest(updatePublicUrl, { public: !isPublic }, csrfToken).then(() => {
      this.setState({ isPublic: !isPublic });
    });
  }

  handleSendConfirmation() {
    const { csrfToken, confirmUrl, user } = this.props;
    postRequest(confirmUrl, { email: user.email }, csrfToken).then(() => {
      this.setState({ confirmationSent: true });
    });
  }

  updateCompletionPercentage() {
    const { questionnaireId, user } = this.props;

    getRequest(
      `/users/${user.resource_id}/completion_percentage?questionnaire_id=${questionnaireId}`,
    ).then((response) => {
      this.setState({
        percentage: response.data.value,
        rolesAvailable: response.data.roles_available,
      });
    });
  }

  updateAvailability(answer) {
    this.setState({ availableNow: answer });
  }

  updateSuggestedTags(tags, type) {
    const { suggestedTags } = this.state;
    const showSuggestionModal = (tags.length ? type : null);

    suggestedTags[type] = tags;

    this.setState({ suggestedTags, showSuggestionModal });
  }

  updateHourlyRate(answer) {
    this.setState({ hourlyRate: answer });
  }

  renderConfirmationAction() {
    const { user, proposalResourceId } = this.props;
    const { confirmationSent } = this.state;
    if (!user.confirmed) {
      if (confirmationSent) {
        return <div className="confirmation-action body-text-2">Confirmation Email Sent!</div>;
      }

      return (
        <div className="confirmation-action body-text-2">
          Oh! You need to confirm your email!
          <a
            className="reconfirm-email-link"
            tabIndex={0}
            role="button"
            onClick={() => this.handleSendConfirmation()}
          >
            {' '}
            Get confirmation email
          </a>
        </div>
      );
    }
    return (
      <div className="confirmation-action">
        {user.confirmed && proposalResourceId && (
        <a className="body-text-2 medium-weight" href={`/proposals/${proposalResourceId}`}>
          <i className="fa fa-chevron-left" />
          Back to proposal
        </a>
        )}
      </div>
    );
  }

  renderProfileActions() {
    const {
      authorized, proposalResourceId, clorifyUrl, isRep, user,
    } = this.props;
    const { editMode, fullView } = this.state;

    if (authorized) {
      return (
        <div className="actions consultant-profile-actions d-none d-md-flex">
          {this.renderConfirmationAction()}
          <div className="right-actions d-flex justify-content-end gap-2 align-items-center">
            <a role="button" tabIndex={0} id={`profile-page-${fullView ? 'public' : 'private'}-view-button`} className="" onClick={() => this.toggleFullView()}>
              {fullView ? <i className="fa fa-eye" /> : <i className="fa fa-eye-slash" />}
              {fullView ? 'View Public Profile' : 'Back to Private View'}
            </a>
            {fullView && isRep
              && (
              <a
                role="button"
                target="_blank"
                tabIndex={0}
                id="view-clorify-button"
                className="btn btn-secondary"
                href={clorifyUrl}
                rel="noreferrer"
              >
                View Clorify
              </a>
              )}
            {fullView && isRep
              && (
              <a
                role="button"
                tabIndex={0}
                id="leave-internal-feedback-button"
                className="btn btn-primary ms-2 me-3"
                href={`/consultant_reviews/new?consultant_id=${user.id}&include_project=false`}
              >
                Leave Feedback
              </a>
              )}
            {fullView
              && (
              <a
                role="button"
                tabIndex={0}
                id="profile-page-edit-button"
                className="btn btn-primary"
                onClick={() => this.toggleEditMode()}
                style={{ height: 'max-content' }}
              >
                {editMode ? 'Finish Editing' : 'Edit Profile'}
              </a>
              )}
          </div>
        </div>
      );
    } if (proposalResourceId !== null) {
      return (
        <div className="actions consultant-profile-actions">
          <a className="body-text-2 medium-weight" href={`/proposals/${proposalResourceId}`}>
            <i className="fa fa-chevron-left" />
            Back to proposal
          </a>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      user,
      csrfToken,
      questionnaireId,
      questionnaireName,
      settingsUrl,
      connectUrl,
      inviteUrl,
      searchEnabled,
      query,
      uninvitedColleagues,
      invitedColleagues,
      workLocationOptions,
      durationOptions,
      timePerWeekOptions,
      inviterEmailDomain,
      previouslyConsultedForEmployer,
      fromProject,
      claimInfoUrl,
      isReadingMore,
      availabilityModalOpen,
    } = this.props;

    const {
      fullView, editMode, percentage, rolesAvailable, isPublic,
      availableNow, hourlyRate, suggestedTags, showSuggestionModal,
    } = this.state;

    return (
      <div className="style-guide consultant-profile container">
        <ToastContainer
          position="bottom-center"
          transition={Slide}
          hideProgressBar
          autoClose={8000}
          closeOnClick
          draggable={false}
          pauseOnHover
          pauseOnFocusLoss
          className="cancelled-interview-toast-container"
          toastClassName="cancelled-interview-toast"
          bodyClassName="cancelled-interview-toast-body"
        />
        {fromProject
          && (
          <a
            id="back-to-project"
            href={`/projects/${fromProject}`}
            className="btn btn-secondary-no-border back-to-project-link"
          >
            <i className="fa fa-chevron-left" />
            <span className="back-to-project-text">Back to project</span>
          </a>
          )}
        {this.renderProfileActions()}
        <div className="consultant-profile-body mt-3">
          <UserSection
            firstName={user.first_name}
            lastName={user.last_name}
            editMode={editMode}
            fullView={fullView}
            csrfToken={csrfToken}
            settingsUrl={settingsUrl}
            inviteUrl={inviteUrl}
            questionableId={user.consultant_profile.id}
            questionableType={user.consultant_profile.questionable_type}
            questionnaireId={questionnaireId}
            questionnaireName={questionnaireName}
            questionCategoryId={user.consultant_profile.user_section_question_category_id}
            userSectionQuestionnaireQuestions={
              user.consultant_profile.user_section_questionnaire_questions
            }
            rolesQuestionnaireQuestions={user.consultant_profile.roles_questionnaire_questions}
            updateCompletionPercentage={this.updateCompletionPercentage}
            rolesAvailable={rolesAvailable}
            togglePublic={this.togglePublic}
            isPublic={isPublic}
            percentage={percentage}
            connectUrl={connectUrl}
            resourceId={user.resource_id}
            availableNow={availableNow && availableNow.value === 'yes'}
            hourlyRate={hourlyRate}
            ratingAvg={user.rating_avg}
            reviewsLength={user.reviews.length}
            searchEnabled={searchEnabled}
            query={query}
            uninvitedColleagues={uninvitedColleagues}
            invitedColleagues={invitedColleagues}
            workLocationOptions={workLocationOptions}
            durationOptions={durationOptions}
            timePerWeekOptions={timePerWeekOptions}
            id={user.consultant_profile.id}
            userId={user.id}
            favoriteId={user.consultant_profile.user_favorite_id}
            inviterEmailDomain={inviterEmailDomain}
            showHireAgain={previouslyConsultedForEmployer}
            suggestedTags={suggestedTags}
            showSuggestionModal={showSuggestionModal}
            updateSuggestedTags={this.updateSuggestedTags}
            fromProject={fromProject}
            consultantInitials={user.name_initials}
            receivedProposals={user.project_invitations}
          />
          <div className="g-row">
            <div className="consultant-profile-left">
              <WorkPreferences
                questionableId={user.consultant_profile.id}
                questionableType={user.consultant_profile.questionable_type}
                questionnaireId={questionnaireId}
                questionnaireName={questionnaireName}
                questionCategoryId={user.consultant_profile.work_preference_question_category_id}
                questionnaireQuestions={
                  user.consultant_profile.work_preferences_questionnaire_questions
}
                fullView={fullView}
                csrfToken={csrfToken}
                claimInfoUrl={claimInfoUrl}
                editMode={editMode}
                userId={user.resource_id}
                updateCompletionPercentage={this.updateCompletionPercentage}
                updateAvailability={this.updateAvailability}
                updateHourlyRate={this.updateHourlyRate}
                showModal={availabilityModalOpen}
              />
              { user.reviews.length ? <Reviews reviews={user.reviews} /> : '' }
              { user.past_clora_projects.length
                ? <PastProjects projects={user.past_clora_projects} /> : ''}
              <AnswersContainer
                categories={user.consultant_profile.left_side_question_categories}
                questionableId={user.consultant_profile.id}
                questionableType={user.consultant_profile.questionable_type}
                fullView={fullView}
                csrfToken={csrfToken}
                editMode={editMode}
                questionnaireId={questionnaireId}
                questionnaireName={questionnaireName}
                updateCompletionPercentage={this.updateCompletionPercentage}
              />
            </div>
            <div className="consultant-profile-right">
              <AnswerSection
                customClass="bio-section"
                key={user.consultant_profile.bio_question_category_id}
                questionCategoryId={user.consultant_profile.bio_question_category_id}
                title="Bio"
                questionableId={user.consultant_profile.id}
                questionableType={user.consultant_profile.questionable_type}
                fullView={fullView}
                csrfToken={csrfToken}
                editMode={editMode}
                questionnaireName={questionnaireName}
                updateCompletionPercentage={this.updateCompletionPercentage}
                suggestedTags={suggestedTags}
                updateSuggestedTags={this.updateSuggestedTags}
                showSuggestionModal={showSuggestionModal}
                isReadingMore={isReadingMore}
              />
              <AnswerSection
                customClass="past-roles-section"
                questionCategoryId={user.consultant_profile.past_roles_category_id}
                questionableId={user.consultant_profile.id}
                questionableType={user.consultant_profile.questionable_type}
                csrfToken={csrfToken}
                questionnaireId={questionnaireId}
                questionnaireName={questionnaireName}
                fullView={fullView}
                editMode={editMode}
                hideFooterButton
                updateCompletionPercentage={this.updateCompletionPercentage}
                isReadingMore={isReadingMore}
              />
              <div>
                <AnswersContainer
                  categories={user.consultant_profile.question_categories}
                  questionableId={user.consultant_profile.id}
                  questionableType={user.consultant_profile.questionable_type}
                  fullView={fullView}
                  csrfToken={csrfToken}
                  editMode={editMode}
                  questionnaireId={questionnaireId}
                  questionnaireName={questionnaireName}
                  updateCompletionPercentage={this.updateCompletionPercentage}
                  suggestedTags={suggestedTags}
                  updateSuggestedTags={this.updateSuggestedTags}
                  showSuggestionModal={showSuggestionModal}
                />
              </div>
            </div>
          </div>
        </div>
        {fullView
          && (
          <div className="sticky-edit d-block d-md-none container-fluid">
            <a
              role="button"
              tabIndex={0}
              id="profile-page-edit-button"
              className="btn btn-primary"
              onClick={() => this.toggleEditMode()}
              style={{ height: 'max-content' }}
            >
              {editMode ? 'Finish Editing' : 'Edit Profile'}
            </a>
          </div>
          )}
      </div>
    );
  }
}

ConsultantProfile.propTypes = {
  percentage: PropTypes.number.isRequired,
  user: PropTypes.shape({}).isRequired,
  confirmUrl: PropTypes.string,
  authorized: PropTypes.bool,
  csrfToken: PropTypes.string,
  questionnaireId: PropTypes.number.isRequired,
  questionnaireName: PropTypes.string.isRequired,
  settingsUrl: PropTypes.string.isRequired,
  inviteUrl: PropTypes.string.isRequired,
  proposalResourceId: PropTypes.string,
  public: PropTypes.bool.isRequired,
  updatePublicUrl: PropTypes.string,
  connectUrl: PropTypes.string.isRequired,
  availableNow: PropTypes.shape({}).isRequired,
  hourlyRate: PropTypes.shape({}).isRequired,
  searchEnabled: PropTypes.bool.isRequired,
  query: PropTypes.number,
  uninvitedColleagues: PropTypes.arrayOf(PropTypes.shape({})),
  invitedColleagues: PropTypes.arrayOf(PropTypes.shape({})),
  workLocationOptions: PropTypes.shape({}).isRequired,
  durationOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  timePerWeekOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  inviterEmailDomain: PropTypes.string,
  previouslyConsultedForEmployer: PropTypes.bool,
  suggestedTags: PropTypes.shape({}),
  clorifyUrl: PropTypes.string.isRequired,
  isRep: PropTypes.bool,
  fromProject: PropTypes.string,
  claimInfoUrl: PropTypes.string.isRequired,
  isReadingMore: PropTypes.bool,
  availabilityModalOpen: PropTypes.bool,
};

ConsultantProfile.defaultProps = {
  authorized: false,
  confirmUrl: '#',
  csrfToken: '',
  proposalResourceId: null,
  updatePublicUrl: '',
  query: null,
  inviterEmailDomain: '',
  previouslyConsultedForEmployer: false,
  suggestedTags: {},
  isRep: false,
  invitedColleagues: [],
  uninvitedColleagues: [],
  fromProject: null,
  isReadingMore: false,
  availabilityModalOpen: false,
};

export default ConsultantProfile;
